// ConvertGraphicFile keeps the Function's Newtonsoft/PascalCase contract.
// Normalize only this endpoint, including its nested metadata. Accept the former
// camelCase contract as a complete shape, never combine conflicting aliases.
export function normalizeDxfResponse(value) {
    const invalid = () => { throw new Error('Invalid DXF conversion response'); };
    const record = value => value && typeof value === 'object' && !Array.isArray(value);
    if (!record(value)) return invalid();
    const pascal = Object.hasOwn(value, 'Result');
    function fields(source, names) {
        if (!record(source)) return invalid();
        const target = {};
        for (const name of names) {
            const upper = name[0].toUpperCase() + name.slice(1);
            if (Object.hasOwn(source, pascal ? name : upper)) return invalid();
            target[name] = source[pascal ? upper : name];
        }
        return target;
    }
    const envelope = fields(value, ['result', 'graphic']);
    const result = fields(envelope.result, ['returnCode', 'message']);
    if (!Number.isInteger(result.returnCode) || typeof result.message !== 'string' ||
        (result.returnCode >= 0 && result.returnCode !== 1 && result.returnCode !== 2)) return invalid();
    const graphic = fields(envelope.graphic, ['drawingObjects', 'noOfDrawingObjects', 'overallLength', 'overallHeight',
        'leftMostX', 'rightMostX', 'topMostY', 'bottomMostY', 'unsupportedObjects', 'noOfDrawingObjectsLimit']);
    if (result.returnCode === -6 && (!Number.isInteger(graphic.noOfDrawingObjects) ||
        !Number.isInteger(graphic.noOfDrawingObjectsLimit))) return invalid();
    if (result.returnCode > 0) {
        if (typeof graphic.drawingObjects !== 'string' || !graphic.drawingObjects ||
            !Number.isInteger(graphic.noOfDrawingObjects) || graphic.noOfDrawingObjects <= 0 ||
            !Number.isInteger(graphic.noOfDrawingObjectsLimit) || graphic.noOfDrawingObjects > graphic.noOfDrawingObjectsLimit ||
            !['overallLength', 'overallHeight', 'leftMostX', 'rightMostX', 'topMostY', 'bottomMostY'].every(key => Number.isFinite(graphic[key])) ||
            graphic.overallLength < 0 || graphic.overallHeight < 0 ||
            !Array.isArray(graphic.unsupportedObjects) || !graphic.unsupportedObjects.every(item => typeof item === 'string')) return invalid();
    }
    return { result, graphic };
}

// The shared unzip helper can resolve null/undefined after a decode failure.
// The drawing reader consumes direct NewDataSet rows by childNodes position,
// including the whitespace nodes emitted by DataTable.WriteXml. Finding fields
// recursively by name is insufficient: a different layout can parse as no objects
// or assign the wrong coordinates. Do not rewrite XML into another format here.
export function isDxfDrawingDocument(document, expectedCount) {
    if (!document?.documentElement || document.getElementsByTagName('parsererror').length) return false;
    const root = document.documentElement;
    if (root.nodeName !== 'NewDataSet' || document.getElementsByTagName('NewDataSet').length !== 1) return false;
    const rows = Array.from(root.childNodes).filter(node => node.nodeName === 'Graphic');
    if (!Number.isInteger(expectedCount) || expectedCount <= 0 || rows.length !== expectedCount ||
        document.getElementsByTagName('Graphic').length !== expectedCount) return false;
    const fields = ['object_type', ...Array.from({ length: 9 }, (_, index) => 'field_' + (index + 1))];
    return rows.every(row => {
        const nodes = Array.from(row.childNodes);
        if (nodes.length !== 21 || !nodes.every((node, index) => index % 2 === 0
            ? node.nodeType === 3 && /^\s*$/.test(node.textContent)
            : node.nodeType === 1 && node.nodeName === fields[(index - 1) / 2] &&
                Array.from(node.childNodes).every(child => child.nodeType === 3 || child.nodeType === 4))) return false;
        const type = nodes[1].textContent;
        const numericFields = { LINE: 4, CIRCLE: 3, ELLIPSE: 4, ARC: 5 }[type];
        return !!numericFields && Array.from({ length: numericFields }, (_, index) => nodes[3 + index * 2].textContent)
            .every(value => value.trim() !== '' && Number.isFinite(Number(value)) && parseFloat(value) === Number(value));
    });
}
